import { ChangeDetectionStrategy, Component, DestroyRef, inject, type OnInit } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { TranslocoDirective } from '@jsverse/transloco';
import { NgbDropdownModule } from '@ng-bootstrap/ng-bootstrap';
import { filter } from 'rxjs/operators';

import { ApprovalRequestService, CurrentUserService } from '../core/services';
import { ROUTER_LINKS } from '../interfaces/router-links.enum';
import { UserRoles } from '../interfaces/user-roles.enum';
import { ProfileHeaderComponent } from '../shared/profile-header/profile-header.component';

const noHeaderRoutes = [/form-builder\/.+/, /forms\/.+/];

@Component({
  selector: 'app-navbar',
  templateUrl: './nav-bar.component.html',
  styleUrl: './nav-bar.component.scss',
  imports: [NgbDropdownModule, ProfileHeaderComponent, RouterLink, RouterLinkActive, TranslocoDirective],
  host: { '[class.hidden]': 'isHidden' },
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NavBarComponent implements OnInit {
  readonly #dr = inject(DestroyRef);
  readonly #currentUser = inject(CurrentUserService);
  readonly #approvalRequestService = inject(ApprovalRequestService);
  readonly #router = inject(Router);

  public isAdmin = false;
  public isEnterprise = false;

  #hidden = false;

  get homeLink(): string {
    return ROUTER_LINKS.HOME;
  }

  get reportsLink(): string {
    return ROUTER_LINKS.REPORTS;
  }

  get staffLink(): string {
    return ROUTER_LINKS.STAFF;
  }

  get teamLink(): string {
    return ROUTER_LINKS.TEAM;
  }

  get reviewLink(): string {
    return ROUTER_LINKS.REGISTRY_REVIEW;
  }

  get analyticsLink(): string {
    return ROUTER_LINKS.ANALYTICS;
  }

  get previewContentLink(): string {
    return ROUTER_LINKS.PREVIEW;
  }

  get isHidden(): boolean {
    return this.#hidden;
  }

  protected readonly pendingApprovalCount = toSignal(
    this.#approvalRequestService.pendingApprovalCount(this.#currentUser.id ?? ''),
    { initialValue: 0 }
  );

  public ngOnInit(): void {
    this.isAdmin = this.#currentUser.profile?.role === UserRoles.Administrator;
    this.isEnterprise = this.#currentUser.tenant!.enterprise || false;

    this.#router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.#dr)
      )
      .subscribe(event => {
        const { url } = event as NavigationEnd;
        noHeaderRoutes.some(route => {
          this.#hidden = route.test(url);
          return this.#hidden;
        });
      });
  }
}
